import config from '@packages/config';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { GlycerinMarketingType } from '@packages/tracking/src/types/enums';
import type {
  GTMEventGlycerinDisplayMarketing,
  GTMEventGlycerinMarketingClick,
  GenericTrackingEvent,
} from '@packages/tracking/src/types/events';
import type { RefObject } from 'react';
import { useIntersectionObserver } from '../useIntersectionObserver/useIntersectionObserver';

type BehaviorProps = {
  behavior_paragraph_marketing_display_type?: 'campaign' | 'ad';
  behavior_paragraph_campaign_type?: string | null;
  behavior_paragraph_campaignid?: string | null;
  // for flyout-menu and usp-bar the campaign-id is underscored
  behavior_paragraph_campaign_id?: string | null;
  behavior_paragraph_category?: string | null;
  behavior_paragraph_detail?: string | null;
  behavior_paragraph_name?: string | null;
};

type PromotionProps = {
  glycerinCampaignType?: string;
  glycerinCampaignId?: string;
  glycerinAdType?: string;
  glycerinAdId?: string;
  glycerinCategory?: string;
  glycerinDetail?: string;
  glycerinName?: string;
  linkReady?: string;
};

type GlycerinCampaignTracking = BehaviorProps | PromotionProps | FinalTrackingData;

type FinalTrackingData = {
  category?: string | null;
  label?: string | null;
  name?: string | null;
  trackingTarget?: string | null;
  detail?: string | null;
  campaignType?: string | null;
  campaignId?: string | null;
  adType?: string | null;
  adId?: string | null;
};

const generateFinalTrackingData = (data: GlycerinCampaignTracking, target: string | undefined) => {
  if ('category' in data) {
    // already final tracking data
    return data;
  }

  let finalTrackingData: FinalTrackingData = {};

  // data from Drupal
  if ('behavior_paragraph_campaign_type' in data) {
    const {
      behavior_paragraph_marketing_display_type: marketingDisplayType,
      behavior_paragraph_category: category,
      behavior_paragraph_name: label,
      behavior_paragraph_detail: detail,
      behavior_paragraph_campaign_type: campaignType,
      behavior_paragraph_campaignid: campaignId,
      behavior_paragraph_campaign_id: campaignIdUnderscored,
    } = data;
    const isAd = marketingDisplayType === 'ad';
    const isCampaign = !isAd; // for campaigns behavior_paragraph_marketing_display_type should be "campaign", but for backwards compatibility, every marketing that is not an ad, is a campaign

    finalTrackingData = {
      category,
      label,
      trackingTarget: target,
      detail,
      ...(isAd && {
        adType: campaignType, // if this is used as ad, use the behavior_paragraph_campaign_type as ad campaign type
        adId: campaignId || campaignIdUnderscored, // if this is used as ad, use the behavior_paragraph_campaignid as ad campaign id
      }),
      ...(isCampaign && {
        campaignType,
        campaignId: campaignId || campaignIdUnderscored,
      }),
    };
  } else if ('glycerinCampaignType' in data || 'glycerinAdType' in data) {
    const {
      glycerinAdId,
      glycerinAdType,
      glycerinCategory,
      glycerinName,
      linkReady,
      glycerinDetail,
      glycerinCampaignType,
      glycerinCampaignId,
    } = data;
    const isAd = glycerinAdId && glycerinAdType;
    const isCampaign = !isAd; // for backwards compatibility, every marketing that is not an ad, is a campaign

    finalTrackingData = {
      category: glycerinCategory,
      label: glycerinName,
      trackingTarget: linkReady,
      detail: glycerinDetail,
      ...(isAd && {
        adType: glycerinAdType,
        adId: glycerinAdId,
      }),
      ...(isCampaign && {
        campaignType: glycerinCampaignType,
        campaignId: glycerinCampaignId,
      }),
    };
  }
  return finalTrackingData;
};

const useReturnOutput = (data: GlycerinCampaignTracking, options: { target?: string } = {}) => {
  const basePath = config.headerInformation?.de?.canonical ?? '';
  const dispatchGtmEvent = useTracking();

  const { campaignType, campaignId, adId, adType, category, trackingTarget, detail, label, name } =
    generateFinalTrackingData(data, options.target);

  if (category) {
    let targetUrl: string | undefined | null;
    try {
      targetUrl = trackingTarget && new URL(trackingTarget, basePath).toString();
    } catch {
      // do nothing
    }

    return () =>
      dispatchGtmEvent<GTMEventGlycerinMarketingClick>({
        event: 'MarketingClick',
        MarketingClickData: {
          marketingType: adType && adId ? GlycerinMarketingType.AD : GlycerinMarketingType.CAMPAIGN,
          ...(campaignType && { campaignType }),
          ...(campaignId && { campaignId }),
          ...(adId && { adId }),
          ...(adType && { adType }),
          category,
          ...(detail && { detail }),
          ...(label && { label }),
          ...(name && { name }),
          ...(targetUrl && { target: targetUrl }),
        },
      });
  }
  return () => {};
};

const executeDisplayTrackingMarketing = (
  data: GlycerinCampaignTracking,
  target: string | undefined,
  dispatchGtmEvent: <T extends GenericTrackingEvent<string | {}>>(event: T) => void,
) => {
  const { category, label, adId, adType, campaignId, campaignType, detail, name } =
    generateFinalTrackingData(data, target);
  if (category) {
    dispatchGtmEvent<GTMEventGlycerinDisplayMarketing>({
      event: 'DisplayMarketing',
      DisplayMarketingData: {
        marketingType: adId && adType ? GlycerinMarketingType.AD : GlycerinMarketingType.CAMPAIGN,
        ...(adType && { adType }),
        ...(adId && { adId }),
        ...(campaignId && { campaignId }),
        ...(campaignType && { campaignType }),
        ...(detail && { detail }),
        ...(label && { label }),
        ...(name && { name }),
        category,
      },
    });
  }
};

/**
 * execute glycerin tracking for marketing display and click on viewport
 *
 * @param object glycerin tracking information
 * @param RefObject<Element> the component reference to observe
 * @param string the click target
 * @returns function to execute the dispatcher on click
 */
const useTrackMarketing = (
  data: GlycerinCampaignTracking,
  componentReference: RefObject<Element | null>,
  target?: string,
  trackOnLoad = false,
) => {
  const dispatchGtmEvent = useTracking();

  useIntersectionObserver(componentReference, {
    intersectOnce: true,
    threshold: 1,
    callback: (isIntersecting) => {
      if (!isIntersecting || trackOnLoad) {
        return;
      }
      // intersectOnce is set to true, so this callback will only be called once
      executeDisplayTrackingMarketing(data, target, dispatchGtmEvent);
    },
  });

  return { trackClick: useReturnOutput(data, { target }) };
};

/**
 * execute glycerin tracking for marketing display and click
 *
 * @param object glycerin tracking information
 * @param string the click target
 * @returns function to execute the dispatcher on click
 */
const useTrackMarketingOnLoad = (data: GlycerinCampaignTracking, target?: string) => {
  const dispatchGtmEvent = useTracking();

  return {
    trackDisplay: () => executeDisplayTrackingMarketing(data, target, dispatchGtmEvent),
    trackClick: useReturnOutput(data, { target }),
  };
};

export { useTrackMarketing, useTrackMarketingOnLoad };
